import { action, computed, observable, set } from "mobx";
import moment from "moment";
import Model from "~/models/Model";
import SubscriptionItem from "./SubscriptionItem";
import SubscriptionStatus from "~/constants/Subscription/SubscriptionStatus";

class Subscription extends Model {
  /**
   * @property {number|null}
   */
  @observable subscriptionId = null;

  /**
   * @property {number|null}
   */
  @observable providerId = null;

  /**
   * @property {number|null}
   */
  @observable customerId = null;

  /**
   * @property {number|null}
   */
  @observable billingCycle = null;

  /**
   * @property {boolean}
   */
  @observable cancelAtPeriodEnd = false;

  /**
   * @property {string|null}
   */
  @observable canceledDts = null;

  /**
   * @property {string|null}
   */
  @observable creationDts = null;

  /**
   * @property {Array}
   */
  @observable subscriptionItems = [];

  /**
   * @property {number|null}
   */
  @observable daysTillNextBillingCycle = null;

  /**
   * @property {string|null}
   */
  @observable nextBillingCycleDate = null;

  /**
   * @property {boolean}
   */
  @observable needsNewBillingCycle = false;

  /**
   * @property {string|null}
   */
  @observable currentBillingCycleStartDate = null;

  /**
   * @property {string|null}
   */
  @observable currentBillingCycleEndDate = null;

  /**
   * @property {string}
   */
  @observable status = SubscriptionStatus.ACTIVE;

  /**
   * @property {number}
   */
  @observable uiMeet3dUsedSeconds = 0;

  /**
   * Set new data to our model
   * @override
   * @param {object} data
   */
  @action
  setData(data) {
    const {
      subscriptionItems,
      creationDts,
      canceledDts,
      nextBillingCycleDate,
      currentBillingCycleStartDate,
      currentBillingCycleEndDate,
      ...rest
    } = data;
    set(this, rest);

    if (subscriptionItems) {
      subscriptionItems.forEach(subscriptionItemArray => {
        const subscriptionItem = new SubscriptionItem(subscriptionItemArray);

        this.subscriptionItems.push(subscriptionItem);
      });
    }

    this.creationDts = creationDts ? moment.unix(creationDts) : null;
    this.canceledDts = canceledDts ? moment.unix(canceledDts) : null;
    this.nextBillingCycleDate = nextBillingCycleDate ? moment(`${nextBillingCycleDate}T00:00:00`) : null;
    this.currentBillingCycleStartDate = currentBillingCycleStartDate
      ? moment(`${currentBillingCycleStartDate}T00:00:00`)
      : null;
    this.currentBillingCycleEndDate = currentBillingCycleEndDate
      ? moment(`${currentBillingCycleEndDate}T00:00:00`)
      : null;
  }

  @computed
  get nextBillingCycleSubscriptionItems() {
    return this.subscriptionItems.filter(
      subscriptionItem =>
        // From the creation of the Subscription
        (subscriptionItem.startedDts === null && subscriptionItem.endedDts === null) ||
        // Started at the past, it hasn't ended yet
        (subscriptionItem.startedDts !== null &&
          subscriptionItem.startedDts.isBefore(moment()) &&
          subscriptionItem.endedDts === null) ||
        // Starts in the future, most probably at the next billing cycle
        (subscriptionItem.startedDts !== null &&
          subscriptionItem.startedDts.isAfter(moment()) &&
          subscriptionItem.endedDts === null),
    );
  }

  @computed
  get downgradedSubscriptionItems() {
    return this.subscriptionItems.filter(
      subscriptionItem =>
        // It will end in the future, still active (at a downgrade)
        subscriptionItem.endedDts !== null && subscriptionItem.endedDts.isAfter(moment()),
    );
  }

  @computed
  get endOfCurrentBillingCycleDate() {
    if (this.nextBillingCycleDate === null) {
      return null;
    }

    const endOfCurrentBillingCycleDate = this.nextBillingCycleDate.clone();
    return endOfCurrentBillingCycleDate.subtract(1, "seconds");
  }
}

export default Subscription;
